import { useEffect, useMemo, useState } from 'react';
import { NumberField, SelectField, SegmentedControl, QuickChips, ResultPanel, ResultItem, ResultNote } from './fields';
import { useAnimatedNumber } from './AnimatedNumber';
import KlineSparkline from './KlineSparkline';
import { PAIRS } from './pairs';

export const PNL_ACCENT = '#7C3AED';

const PROFIT_COLOR = '#34D399';
const LOSS_COLOR = '#FB7185';

export default function ProfitLossCalculator() {
  const [pair, setPair] = useState('EURUSD');
  const [direction, setDirection] = useState('buy');
  const [openPrice, setOpenPrice] = useState(1.08);
  const [closePrice, setClosePrice] = useState(1.085);
  const [lots, setLots] = useState(1);

  const pairMeta = useMemo(() => PAIRS.find((p) => p.value === pair) ?? PAIRS[0], [pair]);

  useEffect(() => {
    setOpenPrice(pairMeta.price);
    setClosePrice(pairMeta.price + pairMeta.pipSize * 30);
  }, [pair, pairMeta]);

  const pips = (closePrice - openPrice) / pairMeta.pipSize;
  const rawPnl = pips * pairMeta.pipValue * lots;
  const pnl = direction === 'buy' ? rawPnl : -rawPnl;
  const profit = pnl >= 0;

  const animatedAbs = useAnimatedNumber(Math.abs(pnl));

  return (
    <>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <SelectField
          label="Cặp tiền tệ"
          value={pair}
          onChange={setPair}
          options={PAIRS.map((p) => ({ value: p.value, label: p.label }))}
          accent={PNL_ACCENT}
        />
        <div>
          <span className="mb-2 block text-[11px] font-medium uppercase tracking-wider text-slate-500">
            Hướng lệnh
          </span>
          <SegmentedControl
            accent={PNL_ACCENT}
            value={direction}
            onChange={setDirection}
            options={[
              { value: 'buy', label: 'Mua (Long)' },
              { value: 'sell', label: 'Bán (Short)' },
            ]}
          />
        </div>
        <NumberField label="Giá vào lệnh" value={openPrice} onChange={setOpenPrice} min={0} step={0.0001} accent={PNL_ACCENT} />
        <NumberField label="Giá đóng lệnh" value={closePrice} onChange={setClosePrice} min={0} step={0.0001} accent={PNL_ACCENT} />
        <div className="sm:col-span-2">
          <NumberField
            label="Khối lượng lệnh"
            value={lots}
            onChange={setLots}
            suffix="lot"
            min={0.01}
            step={0.01}
            accent={PNL_ACCENT}
          />
          <div className="mt-2">
            <QuickChips options={[0.01, 0.1, 0.5, 1, 2]} value={lots} onChange={setLots} />
          </div>
        </div>
      </div>

      <div className="mt-6">
        <KlineSparkline
          openPrice={openPrice}
          closePrice={closePrice}
          pipSize={pairMeta.pipSize}
          direction={direction}
          profit={profit}
        />
        <div className="mt-3">
          <ResultPanel accent={profit ? PROFIT_COLOR : LOSS_COLOR}>
            <ResultItem
              label={profit ? 'Lợi nhuận ước tính' : 'Thua lỗ ước tính'}
              value={`${profit ? '+' : '-'}$${animatedAbs.toFixed(2)}`}
              accent={profit ? PROFIT_COLOR : LOSS_COLOR}
              big
            />
            <ResultItem label="Biến động giá" value={`${pips.toFixed(1)} pips`} accent={PNL_ACCENT} />
            <ResultNote>
              Hướng {direction === 'buy' ? 'Mua' : 'Bán'} · {pairMeta.label} · {lots} lot
            </ResultNote>
          </ResultPanel>
        </div>
      </div>
    </>
  );
}